import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Shield,
  Clock,
  Wrench,
  FileText,
  CheckCircle2,
  ExternalLink,
  Eye,
  Loader2,
  Calendar,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import type { AssetAlert, AlertType } from '@/types/alerts';
import { getAlertsForAsset, generateAlertsForAsset, getComplianceAlertsForAsset } from '@/services/alerts-service';
import type { Asset } from '@/types/asset';
import { useNavigate } from 'react-router-dom';

interface AssetAlertsTabProps {
  readonly assetId: string;
  readonly assetName: string;
  readonly asset?: Asset;
  readonly disabled?: boolean;
  readonly onAlertsChange?: (alertCount: number) => void;
}

const alertTypeConfig = {
  audit: {
    label: 'Audit',
    icon: FileText,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950/20',
    borderColor: 'border-red-200 dark:border-red-800',
    lightBg: 'bg-red-50/50 dark:bg-red-950/10',
  },
  checkinout: {
    label: 'Check-In/Out',
    icon: Clock,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
    borderColor: 'border-orange-200 dark:border-orange-800',
    lightBg: 'bg-orange-50/50 dark:bg-orange-950/10',
  },
  maintenance: {
    label: 'Maintenance',
    icon: Wrench,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    lightBg: 'bg-blue-50/50 dark:bg-blue-950/10',
  },
  insurance: {
    label: 'Insurance',
    icon: Shield,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-950/20',
    borderColor: 'border-purple-200 dark:border-purple-800',
    lightBg: 'bg-purple-50/50 dark:bg-purple-950/10',
  },
  warranty: {
    label: 'Warranty',
    icon: Shield,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    borderColor: 'border-green-200 dark:border-green-800',
    lightBg: 'bg-green-50/50 dark:bg-green-950/10',
  },
  compliance: {
    label: 'Compliance',
    icon: Shield,
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-50 dark:bg-teal-950/20',
    borderColor: 'border-teal-200 dark:border-teal-800',
    lightBg: 'bg-teal-50/50 dark:bg-teal-950/10',
  },
};

const severityConfig = {
  critical: {
    label: 'Critical',
    icon: AlertTriangle,
    color: 'text-red-600 dark:text-red-400',
    badgeVariant: 'destructive' as const,
  },
  warning: {
    label: 'Warning',
    icon: AlertCircle,
    color: 'text-yellow-600 dark:text-yellow-400',
    badgeVariant: 'default' as const,
  },
  info: {
    label: 'Info',
    icon: Info,
    color: 'text-blue-600 dark:text-blue-400',
    badgeVariant: 'secondary' as const,
  },
};

// Shared helper function for severity icon styling (S4144)
function getSeverityIconStyle(severity: 'critical' | 'warning' | 'info') {
  if (severity === 'critical') {
    return {
      bgColor: 'bg-red-100 dark:bg-red-900/30',
      iconColor: 'text-red-600 dark:text-red-400',
    };
  } else if (severity === 'warning') {
    return {
      bgColor: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
    };
  }
  return {
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
  };
}

// Shared helper function for severity badge styling (S4144)
function getSeverityBadgeStyle(severity: 'critical' | 'warning' | 'info') {
  if (severity === 'critical') {
    return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
  } else if (severity === 'warning') {
    return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
  }
  return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
}

export function AssetAlertsTab({ assetId, asset, onAlertsChange }: AssetAlertsTabProps) {
  const [alerts, setAlerts] = useState<AssetAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState<AssetAlert | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'acknowledge' | 'resolve'>('acknowledge');
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | AlertType>('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadAlerts();
  }, [assetId]);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const baseAlerts = await getAlertsForAsset(assetId);
      const complianceAlerts = await getComplianceAlertsForAsset(assetId);

      let combined = [...baseAlerts];
      
      // If no alerts from API and we have asset data, generate some from asset properties
      if (combined.length === 0 && asset) {
        combined = generateAlertsForAsset(asset);
      }

      // Always include compliance alerts
      combined = [...combined, ...complianceAlerts];
      
      setAlerts(combined);
      
      // Notify parent of alert count
      const activeAlerts = combined.filter(a => a.status === 'active');
      if (onAlertsChange) {
        onAlertsChange(activeAlerts.length);
      }
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (alert: AssetAlert) => {
    setSelectedAlert(alert);
    setDetailDialogOpen(true);
  };

  const handleNavigateToAudit = (alert?: AssetAlert) => {
    if (alert?.auditData?.auditId) {
      // Navigate to audit page with audit highlighted
      navigate(`/audits?assetId=${assetId}&auditId=${alert.auditData.auditId}`);
    } else {
      // Navigate to general audits page filtered by this asset
      navigate(`/audits?assetId=${assetId}`);
    }
  };

  const handleNavigateToCheckInOut = (alert: AssetAlert) => {
    if (alert.checkInOutData?.checkOutRecordId) {
      navigate(`/checkinout?recordId=${alert.checkInOutData.checkOutRecordId}`);
    } else {
      navigate('/checkinout');
    }
  };

  const handleAcknowledge = (alert: AssetAlert) => {
    setSelectedAlert(alert);
    setActionType('acknowledge');
    setActionDialogOpen(true);
  };

  const handleResolve = (alert: AssetAlert) => {
    setSelectedAlert(alert);
    setActionType('resolve');
    setActionDialogOpen(true);
  };

  const handleActionSubmit = async () => {
    if (!selectedAlert) return;
    
    setActionLoading(true);
    try {
      // TODO: Implement API call to acknowledge/resolve alert
      console.log(`${actionType} alert:`, selectedAlert.id, actionNotes);
      
      // Reload alerts after action
      await loadAlerts();
      
      // Close dialogs
      setActionDialogOpen(false);
      setDetailDialogOpen(false);
      setActionNotes('');
    } catch (error) {
      console.error(`Failed to ${actionType} alert:`, error);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAlerts = activeTab === 'all'
    ? alerts 
    : alerts.filter(a => a.type === activeTab);

  const activeAlerts = filteredAlerts.filter(a => a.status === 'active');
  const acknowledgedAlerts = filteredAlerts.filter(a => a.status === 'acknowledged');
  const resolvedAlerts = filteredAlerts.filter(a => a.status === 'resolved');

  // Calculate alert counts by type
  const alertCounts = {
    all: alerts.filter(a => a.status === 'active').length,
    audit: alerts.filter(a => a.type === 'audit' && a.status === 'active').length,
    maintenance: alerts.filter(a => a.type === 'maintenance' && a.status === 'active').length,
    insurance: alerts.filter(a => a.type === 'insurance' && a.status === 'active').length,
    warranty: alerts.filter(a => a.type === 'warranty' && a.status === 'active').length,
    compliance: alerts.filter(a => a.type === 'compliance' && a.status === 'active').length,
  };

  // Calculate summary statistics (available for future use)
  // const criticalCount = activeAlerts.filter(a => a.severity === 'critical').length;
  // const warningCount = activeAlerts.filter(a => a.severity === 'warning').length;
  // const overdueCount = activeAlerts.filter(a => a.daysOverdue && a.daysOverdue > 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="relative border border-border/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Asset Alerts
              </CardTitle>
              <CardDescription>
                View all alerts and notifications for this asset
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Alert Type Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="space-y-6">
            <TabsList className="inline-flex h-auto w-full justify-start gap-1 rounded-lg bg-muted/50 p-1">
              <TabsTrigger 
                value="all" 
                className="relative rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <span>All Alerts</span>
                {alertCounts.all > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                    {alertCounts.all}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="audit"
                className="relative rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <FileText className="mr-1.5 h-3.5 w-3.5" />
                <span>Audit</span>
                {alertCounts.audit > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                    {alertCounts.audit}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="maintenance"
                className="relative rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Wrench className="mr-1.5 h-3.5 w-3.5" />
                <span>Maintenance</span>
                {alertCounts.maintenance > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                    {alertCounts.maintenance}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="insurance"
                className="relative rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Shield className="mr-1.5 h-3.5 w-3.5" />
                <span>Insurance</span>
                {alertCounts.insurance > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                    {alertCounts.insurance}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="warranty"
                className="relative rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Shield className="mr-1.5 h-3.5 w-3.5" />
                <span>Warranty</span>
                {alertCounts.warranty > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                    {alertCounts.warranty}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="compliance"
                className="relative rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Shield className="mr-1.5 h-3.5 w-3.5" />
                <span>Compliance</span>
                {alertCounts.compliance > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                    {alertCounts.compliance}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="space-y-4">
              {/* Scrollable container for alerts - updated for better vertical space usage */}
              <div className="max-h-[600px] overflow-y-auto pr-2 space-y-4">
                {/* Active Alerts */}
                {activeAlerts.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between sticky top-0 bg-background z-10 pb-2">
                      <h3 className="text-sm font-semibold text-foreground">Active Alerts ({activeAlerts.length})</h3>
                      {activeTab === 'audit' && activeAlerts.length > 0 && (
                        <Button size="sm" variant="outline" onClick={() => handleNavigateToAudit()}>
                          <ExternalLink className="mr-2 h-3 w-3" />
                          View All Audits
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                    {activeAlerts.map((alert) => (
                        <CompactAlertCard
                        key={alert.id}
                        alert={alert}
                        onViewDetails={handleViewDetails}
                        onAcknowledge={handleAcknowledge}
                        onResolve={handleResolve}
                        onNavigateToAudit={handleNavigateToAudit}
                        onNavigateToCheckInOut={handleNavigateToCheckInOut}
                      />
                    ))}
                  </div>
                  </div>
                )}

                {/* Acknowledged Alerts */}
                {acknowledgedAlerts.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">Acknowledged ({acknowledgedAlerts.length})</h3>
                    <div className="space-y-2">
                    {acknowledgedAlerts.map((alert) => (
                        <CompactAlertCard
                        key={alert.id}
                        alert={alert}
                        onViewDetails={handleViewDetails}
                        onResolve={handleResolve}
                        onNavigateToAudit={handleNavigateToAudit}
                        onNavigateToCheckInOut={handleNavigateToCheckInOut}
                      />
                    ))}
                  </div>
                  </div>
                )}

                {/* Resolved Alerts */}
                {resolvedAlerts.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">Resolved ({resolvedAlerts.length})</h3>
                    <div className="space-y-2">
                    {resolvedAlerts.map((alert) => (
                        <CompactAlertCard
                        key={alert.id}
                        alert={alert}
                        onViewDetails={handleViewDetails}
                        onNavigateToAudit={handleNavigateToAudit}
                        onNavigateToCheckInOut={handleNavigateToCheckInOut}
                      />
                    ))}
                  </div>
                  </div>
                )}

                {/* No Alerts */}
                {filteredAlerts.length === 0 && (
                  <div className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                        <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-1">All Clear!</h3>
                        <p className="text-sm text-muted-foreground">
                          No {activeTab !== 'all' ? activeTab : ''} alerts for this asset
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Alert Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Alert Details</DialogTitle>
          </DialogHeader>
          {selectedAlert && <AlertDetailView alert={selectedAlert} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setDetailDialogOpen(false);
              if (selectedAlert?.type === 'audit') {
                handleNavigateToAudit(selectedAlert);
              } else if (selectedAlert?.type === 'checkinout') {
                handleNavigateToCheckInOut(selectedAlert);
              } else {
                handleNavigateToAudit();
              }
            }}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {actionType === 'acknowledge' ? 'Acknowledge' : 'Resolve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface CompactAlertCardProps {
  readonly alert: AssetAlert;
  readonly onViewDetails: (alert: AssetAlert) => void;
  readonly onAcknowledge?: (alert: AssetAlert) => void;
  readonly onResolve?: (alert: AssetAlert) => void;
  readonly onNavigateToAudit?: (alert?: AssetAlert) => void;
  readonly onNavigateToCheckInOut?: (alert: AssetAlert) => void;
}

function CompactAlertCard({ 
  alert, 
  onViewDetails, 
  onNavigateToAudit,
  onNavigateToCheckInOut,
}: Readonly<CompactAlertCardProps>) {
  const typeConfig = alertTypeConfig[alert.type];
  const severityConfig_ = severityConfig[alert.severity];
  const TypeIcon = typeConfig.icon;

  const handleNavigate = () => {
    if (alert.type === 'audit' && onNavigateToAudit) {
      onNavigateToAudit(alert);
    } else if (alert.type === 'checkinout' && onNavigateToCheckInOut) {
      onNavigateToCheckInOut(alert);
    }
  };

  // Determine background color based on severity (subtle colors)
  const getSeverityBg = () => {
    if (alert.severity === 'critical') {
      return 'bg-red-50/50 dark:bg-red-950/10 border-l-red-500';
    } else if (alert.severity === 'warning') {
      return 'bg-amber-50/50 dark:bg-amber-950/10 border-l-amber-500';
    }
    return 'bg-green-50/50 dark:bg-green-950/10 border-l-green-500';
  };

  const iconStyle = getSeverityIconStyle(alert.severity);

  return (
    <div className={cn(
      'group relative rounded-lg border-l-4 border p-3 transition-all hover:shadow-md',
      'h-[88px] overflow-hidden',
      getSeverityBg()
    )}>
      <div className="flex items-center gap-3 h-full min-h-0">
        {/* Icon - severity-based color */}
        <div className={cn('rounded-lg p-1.5 shrink-0 self-start', iconStyle.bgColor)}>
          <TypeIcon className={cn('h-4 w-4', iconStyle.iconColor)} />
          </div>
          
        {/* Main Content - Title and Description */}
        <div className="flex-1 min-w-0 flex flex-col justify-center overflow-hidden">
          <div className="flex items-start gap-2 mb-1">
            <h4 className="font-semibold text-sm truncate flex-1">{alert.title}</h4>
            <div className="flex items-center gap-1 shrink-0">
              <Badge className={cn('text-xs border', getSeverityBadgeStyle(alert.severity))}>
                {severityConfig_.label}
              </Badge>
              <Badge variant="outline" className="text-xs">{typeConfig.label}</Badge>
              {alert.status !== 'active' && (
                <Badge variant="secondary" className="text-xs">{alert.status}</Badge>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-1">{alert.description}</p>
        </div>

        {/* Right Side - Details and Actions */}
        <div className="flex items-center gap-3 shrink-0 self-start">
          {/* Metadata */}
          <div className="flex flex-col items-end gap-1 text-xs text-muted-foreground">
              {alert.dueDate && (
              <span className="flex items-center gap-1 whitespace-nowrap">
                <Calendar className="h-3 w-3" />
                {format(parseISO(alert.dueDate), 'dd MMM yyyy')}
              </span>
              )}
              {alert.daysOverdue && alert.daysOverdue > 0 && (
              <span className="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium whitespace-nowrap">
                <AlertTriangle className="h-3 w-3" />
                  {alert.daysOverdue} days overdue
                </span>
              )}
            {alert.auditData && !alert.dueDate && (
              <span className="flex items-center gap-1 whitespace-nowrap">
                <FileText className="h-3 w-3" />
                {alert.auditData.auditName}
              </span>
            )}
            </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => onViewDetails(alert)}>
              <Eye className="h-4 w-4" />
              </Button>
              
              {alert.canNavigate && (alert.type === 'audit' || alert.type === 'checkinout') && (
              <Button size="sm" variant="default" className="h-8" onClick={handleNavigate}>
                <ExternalLink className="mr-1.5 h-3 w-3" />
                View
                </Button>
              )}
            </div>
          </div>
        </div>
    </div>
  );
}

function AlertDetailView({ alert }: Readonly<{ alert: AssetAlert }>) {
  const typeConfig = alertTypeConfig[alert.type];
  const severityConfig_ = severityConfig[alert.severity];
  const TypeIcon = typeConfig.icon;
  const SeverityIcon = severityConfig_.icon;

  const iconStyle = getSeverityIconStyle(alert.severity);

  return (
    <ScrollArea className="max-h-[60vh]">
      <div className="space-y-4 pr-4">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className={cn('rounded-lg p-3', iconStyle.bgColor)}>
            <TypeIcon className={cn('h-6 w-6', iconStyle.iconColor)} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{alert.title}</h3>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={cn('border', getSeverityBadgeStyle(alert.severity))}>
                <SeverityIcon className="mr-1 h-3 w-3" />
                {severityConfig_.label}
              </Badge>
              <Badge variant="outline">{typeConfig.label}</Badge>
              {alert.status !== 'active' && (
                <Badge variant="secondary">{alert.status}</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
          <p className="text-sm">{alert.description}</p>
        </div>

        {/* Timeline Information */}
        <div className="grid gap-3 sm:grid-cols-2">
        {alert.dueDate && (
            <div className="space-y-1">
              <h4 className="text-sm font-medium text-muted-foreground">Due Date</h4>
              <p className="text-sm font-medium">{format(parseISO(alert.dueDate), 'dd MMMM yyyy')}</p>
          </div>
        )}

        {alert.daysOverdue && alert.daysOverdue > 0 && (
            <div className="space-y-1">
              <h4 className="text-sm font-medium text-muted-foreground">Days Overdue</h4>
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              {alert.daysOverdue} days
            </p>
          </div>
        )}

          <div className="space-y-1">
            <h4 className="text-sm font-medium text-muted-foreground">Created</h4>
            <p className="text-sm">{format(parseISO(alert.createdDate), 'dd MMM yyyy HH:mm')}</p>
          </div>
        </div>

        {/* Type-specific details */}
        {alert.auditData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Audit Details
            </h4>
            <div className="grid gap-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Audit Name:</span>
                <span className="col-span-2 font-medium">{alert.auditData.auditName}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Audit Type:</span>
                <span className="col-span-2 font-medium">{alert.auditData.auditType}</span>
              </div>
              {alert.auditData.assignedDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Assigned Date:</span>
                  <span className="col-span-2">{format(parseISO(alert.auditData.assignedDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.auditData.dueDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Due Date:</span>
                  <span className="col-span-2">{format(parseISO(alert.auditData.dueDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.auditData.assignedTo && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Assigned To:</span>
                  <span className="col-span-2">{alert.auditData.assignedTo}</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Status:</span>
                <span className="col-span-2">
                  <Badge variant={
                    (() => {
                      if (alert.auditData.auditStatus === 'completed') return 'secondary';
                      if (alert.auditData.auditStatus === 'exception') return 'destructive';
                      if (alert.auditData.auditStatus === 'awaiting-review') return 'default';
                      return 'outline';
                    })()
                  }>
                    {alert.auditData.auditStatus}
                  </Badge>
                </span>
              </div>
              {alert.auditData.exceptionReason && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Exception Reason:</span>
                  <span className="col-span-2 text-red-600 dark:text-red-400">{alert.auditData.exceptionReason}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {alert.checkInOutData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Check-In/Out Details
            </h4>
            <div className="grid gap-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Assigned To:</span>
                <span className="col-span-2 font-medium">{alert.checkInOutData.assignedToName}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Checked Out:</span>
                <span className="col-span-2">{format(parseISO(alert.checkInOutData.checkOutDate), 'dd MMM yyyy')}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Expected Return:</span>
                <span className="col-span-2">{format(parseISO(alert.checkInOutData.expectedReturnDate), 'dd MMM yyyy')}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Current Location:</span>
                <span className="col-span-2 font-medium">{alert.checkInOutData.currentLocation}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Status:</span>
                <span className="col-span-2">
                  <Badge variant={
                    (() => {
                      if (alert.checkInOutData.checkOutStatus === 'overdue') return 'destructive';
                      if (alert.checkInOutData.checkOutStatus === 'due-soon') return 'default';
                      return 'secondary';
                    })()
                  }>
                    {alert.checkInOutData.checkOutStatus}
                  </Badge>
                </span>
              </div>
            </div>
          </div>
        )}

        {alert.insuranceData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Insurance Details
            </h4>
            <div className="grid gap-3 text-sm">
              {alert.insuranceData.policyNumber && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Policy Number:</span>
                  <span className="col-span-2 font-medium">{alert.insuranceData.policyNumber}</span>
                </div>
              )}
              {alert.insuranceData.provider && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="col-span-2">{alert.insuranceData.provider}</span>
                </div>
              )}
              {alert.insuranceData.expiryDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Expiry Date:</span>
                  <span className="col-span-2">{format(parseISO(alert.insuranceData.expiryDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.insuranceData.daysUntilExpiry !== undefined && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Days Until Expiry:</span>
                  <span className={cn(
                    "col-span-2 font-medium",
                    alert.insuranceData.daysUntilExpiry <= 30 && "text-red-600 dark:text-red-400"
                  )}>
                    {alert.insuranceData.daysUntilExpiry} days
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {alert.warrantyData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Warranty Details
            </h4>
            <div className="grid gap-3 text-sm">
              {alert.warrantyData.provider && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="col-span-2">{alert.warrantyData.provider}</span>
                </div>
              )}
              {alert.warrantyData.expiryDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Expiry Date:</span>
                  <span className="col-span-2">{format(parseISO(alert.warrantyData.expiryDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.warrantyData.daysUntilExpiry !== undefined && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Days Until Expiry:</span>
                  <span className={cn(
                    "col-span-2 font-medium",
                    alert.warrantyData.daysUntilExpiry <= 30 && "text-red-600 dark:text-red-400"
                  )}>
                    {alert.warrantyData.daysUntilExpiry} days
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {alert.maintenanceData && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Maintenance Details
            </h4>
            <div className="grid gap-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Type:</span>
                <span className="col-span-2 font-medium">{alert.maintenanceData.maintenanceType}</span>
              </div>
              {alert.maintenanceData.lastServiceDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Last Service:</span>
                  <span className="col-span-2">{format(parseISO(alert.maintenanceData.lastServiceDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.maintenanceData.nextServiceDate && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Next Service:</span>
                  <span className="col-span-2">{format(parseISO(alert.maintenanceData.nextServiceDate), 'dd MMM yyyy')}</span>
                </div>
              )}
              {alert.maintenanceData.serviceProvider && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Service Provider:</span>
                  <span className="col-span-2">{alert.maintenanceData.serviceProvider}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status History */}
        {(alert.acknowledgedDate || alert.resolvedDate) && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <h4 className="text-sm font-semibold">Status History</h4>
            <div className="space-y-2 text-sm">
        {alert.acknowledgedDate && (
          <div>
                  <p className="text-muted-foreground">Acknowledged</p>
                  <p className="font-medium">
              {format(parseISO(alert.acknowledgedDate), 'dd MMM yyyy HH:mm')}
              {alert.acknowledgedBy && ` by ${alert.acknowledgedBy}`}
            </p>
          </div>
        )}

        {alert.resolvedDate && (
          <div>
                  <p className="text-muted-foreground">Resolved</p>
                  <p className="font-medium">
              {format(parseISO(alert.resolvedDate), 'dd MMM yyyy HH:mm')}
              {alert.resolvedBy && ` by ${alert.resolvedBy}`}
            </p>
          </div>
        )}
            </div>
          </div>
        )}

        {/* Notes */}
        {alert.notes && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Notes</h4>
            <p className="text-sm whitespace-pre-wrap rounded-lg border p-3 bg-muted/50">{alert.notes}</p>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
